// Поток событий кампании на стороне клиента (D2).
//
// Не `EventSource`: он не умеет заголовков, а удостоверение здесь —
// заголовок (токен мастера или устройство игрока). Класть токен
// в строку запроса нельзя по той же причине, по которой токен
// из письма живёт во фрагменте (D38): строка запроса попадает
// в логи прокси и в `Referer`.
//
// Поэтому поток читается обычным `fetch` с телом-потоком. Взамен
// приходится делать руками то, что `EventSource` делает сам:
// разбор кадров и переподключение. Разбор — двадцать строк,
// переподключение всё равно пришлось бы писать своё: нам нужен
// не «подключись заново», а «продолжи с номера, на котором
// остановился».

import { campaignEventsPath, campaignStreamFrameSchema } from '@rollcall/contracts'
import type { CampaignEvent, CampaignStreamFrame } from '@rollcall/contracts'

/**
 * Что человек видит в углу экрана.
 *
 * Три состояния, и все три разные: `live` — события идут, `connecting` —
 * связи нет, но мы её ищем, `offline` — не пускают, и сами мы этого
 * не исправим. Без такого признака «ничего не происходит»
 * не отличить от «оборвалось».
 */
export type StreamState = 'connecting' | 'live' | 'offline'

export type StreamHandlers = {
  /** Событие кампании по порядку номеров. */
  readonly onEvent: (event: CampaignEvent) => void
  /**
   * «Перечитай состояние целиком»: пропущено слишком много.
   * Приходит и при первом подключении с нулевым номером — клиент
   * и так читает состояние запросом.
   */
  readonly onResync: (lastSeq: number) => void
  readonly onState: (state: StreamState) => void
}

export type StreamOptions = {
  readonly campaignId: string
  /** Номер, с которого продолжать. Ноль — «я ничего не видел». */
  readonly after: number
  readonly headers: () => Record<string, string>
  readonly fetch?: typeof globalThis.fetch
  readonly baseUrl?: string
  /** Паузы между попытками; последняя повторяется дальше. */
  readonly backoffMs?: readonly number[]
  readonly sleep?: (ms: number) => Promise<void>
  /**
   * Что делать, если поток ответил «не авторизован».
   *
   * Токен доступа живёт минуты, а поток — часы, и рано или поздно
   * он протухнет прямо в соединении. `true` — токен обновлён, стоит
   * попробовать снова; `false` — обновиться не вышло, дальше только
   * выход.
   */
  readonly onUnauthorized?: () => Promise<boolean>
}

/**
 * Паузы переподключения.
 *
 * Первая короткая: обычный обрыв — это смена вышки или выход
 * из туннеля, и связь возвращается через секунду. Дальше реже:
 * лежащий сервер от частых попыток не поднимется, а телефон
 * от них разрядится.
 */
export const RECONNECT_BACKOFF_MS: readonly number[] = [1_000, 2_000, 5_000, 15_000]

export type CampaignStream = {
  /** Номер последнего применённого события. */
  seq(): number
  /**
   * Проснуться: подключиться заново, не досиживая паузу.
   *
   * Нужно после возвращения вкладки из фона. Телефон замораживает
   * таймеры и рвёт соединения молча: страница возвращается, поток
   * выглядит живым, а событий по нему больше не будет. Единственный
   * способ узнать это — подключиться заново, и повод к этому знает
   * не поток, а экран.
   */
  wake(): void
  stop(): void
}

const sleepFor = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Разбор потока SSE.
 *
 * Кадр отделяется пустой строкой, поля — переводом строки. Нам нужны
 * только `data`: вид кадра лежит внутри полезной нагрузки, потому что
 * иначе он существовал бы в двух местах сразу и однажды разошёлся.
 * Строки, начинающиеся с двоеточия, — сердцебиение, и это не пустота:
 * по ним видно, что соединение живо.
 */
export function parseFrames(chunk: string): string[] {
  return chunk
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).trim())
}

/**
 * Подключиться и держать подключение.
 *
 * Возвращает управление сразу: поток живёт в фоне, пока не остановят.
 * Номер последнего события хранится здесь же — переподключение
 * продолжает с него, а не с того, с чего начинали.
 */
export function openCampaignStream(
  options: StreamOptions,
  handlers: StreamHandlers,
): CampaignStream {
  const doFetch = options.fetch ?? globalThis.fetch
  const backoff = options.backoffMs ?? RECONNECT_BACKOFF_MS
  const sleep = options.sleep ?? sleepFor
  const baseUrl = options.baseUrl ?? ''

  let seq = options.after
  let stopped = false
  let attempt = 0
  let hurry = false
  let abort: AbortController | null = null

  const apply = (frame: CampaignStreamFrame): void => {
    if (frame.frame === 'ready') {
      handlers.onState('live')
      return
    }
    if (frame.frame === 'resync') {
      seq = frame.lastSeq
      handlers.onResync(frame.lastSeq)
      return
    }
    // Номер не убывает: повторно пришедшее событие пропускается.
    // Это и есть идемпотентность по `seq` (architecture.md, раздел 4).
    if (frame.event.seq <= seq) return
    seq = frame.event.seq
    handlers.onEvent(frame.event)
  }

  const readOnce = async (): Promise<void> => {
    abort = new AbortController()
    const response = await doFetch(
      `${baseUrl}${campaignEventsPath(options.campaignId, seq)}`,
      {
        headers: { accept: 'text/event-stream', ...options.headers() },
        signal: abort.signal,
      },
    )

    if (!response.ok || response.body === null) {
      // Отказ доступа — не обрыв: переподключением он не лечится.
      const fatal =
        response.status === 401 || response.status === 403 || response.status === 404
      throw new StreamRefused(response.status, fatal)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let rest = ''

    for (;;) {
      const { done, value } = await reader.read()
      if (done) return
      rest += decoder.decode(value, { stream: true })

      // Кадр может прийти половиной: хвост без пустой строки
      // остаётся до следующего куска.
      const parts = rest.split('\n\n')
      rest = parts.pop() ?? ''
      for (const part of parts) {
        for (const data of parseFrames(part)) {
          const parsed = campaignStreamFrameSchema.safeParse(JSON.parse(data))
          if (!parsed.success) continue
          // Счётчик попыток сбрасывает не удачное подключение,
          // а полученный кадр. Разница видна на моргающем прокси:
          // соединение открывается и тут же рвётся, кадров нет —
          // и пауза обязана расти, а не оставаться секундной.
          attempt = 0
          apply(parsed.data)
        }
      }
    }
  }

  const loop = async (): Promise<void> => {
    while (!stopped) {
      handlers.onState('connecting')
      try {
        await readOnce()
      } catch (error) {
        if (stopped) return
        if (error instanceof StreamRefused && error.status === 401) {
          // Токен протух прямо в соединении: поток живёт часами,
          // а токен — минуты. Обновились — продолжаем с того же
          // номера; нет — дальше только выход.
          const recovered = (await options.onUnauthorized?.()) ?? false
          if (!recovered) {
            handlers.onState('offline')
            return
          }
          attempt = 0
          continue
        }
        if (error instanceof StreamRefused && error.fatal) {
          // Нас не пускают: места больше нет, кампания чужая.
          // Стучаться дальше нечего — экран скажет, что связи нет.
          handlers.onState('offline')
          return
        }
      }
      if (stopped) return
      handlers.onState('connecting')
      if (hurry) {
        // Вкладка вернулась из фона: ждать нечего, связь могла
        // появиться минуту назад.
        hurry = false
        attempt = 0
        continue
      }
      // Пауза нарастает и упирается в последнюю: держать телефон
      // в вечном переподключении дороже, чем ждать полминуты.
      const wait = backoff[Math.min(attempt, backoff.length - 1)] ?? 15_000
      attempt += 1
      await sleep(wait)
    }
  }

  void loop()

  return {
    seq: () => seq,
    wake() {
      if (stopped) return
      hurry = true
      // Рвём текущую попытку: соединение могло умереть молча,
      // и ждать от него ошибки можно до самого таймаута.
      abort?.abort()
    },
    stop() {
      stopped = true
      abort?.abort()
      handlers.onState('offline')
    },
  }
}

/** Отказ потока: код ответа и можно ли лечить переподключением. */
export class StreamRefused extends Error {
  constructor(
    readonly status: number,
    readonly fatal: boolean,
  ) {
    super(`поток событий отказал: ${status}`)
    this.name = 'StreamRefused'
  }
}
